import React, { useState, useEffect } from 'react';
import portalAPI from '../services/portalAPI';

interface NearestTerminal {
  code: string;
  name: string;
  carrier: string;
  carrier_name: string;
  address: string;
  phone: string;
  region?: string;
}

interface DeliveryCheckResponse {
  has_zone: boolean;
  carrier: string | null;
  carrier_code: string | null;
  is_fastway_direct: boolean;
  needs_handoff: boolean;
  is_limited: boolean;
  is_single_day: boolean;
  delivery_days: string | null;
  delivery_type: string | null;
  next_delivery_date: string | null;
  next_delivery_date_iso: string | null;
  days_until_delivery: number | null;
  remote_fee: number | null;
  city: string | null;
  state: string | null;
  warning_message: string | null;
  warning_level: 'info' | 'warning' | 'critical';
  suggest_will_call: boolean;
  nearest_terminals: NearestTerminal[];
}

interface DeliveryWarningBannerProps {
  customerId: number;
  onSuggestWillCall?: (terminals: NearestTerminal[]) => void;
  className?: string;
  style?: React.CSSProperties;
  compact?: boolean;
}

const DeliveryWarningBanner: React.FC<DeliveryWarningBannerProps> = ({
  customerId,
  onSuggestWillCall,
  className = '',
  style = {},
  compact = false,
}) => {
  const [data, setData] = useState<DeliveryCheckResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTerminals, setShowTerminals] = useState(false);

  useEffect(() => {
    const fetchDeliveryCheck = async () => {
      if (!customerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await portalAPI.get(`/orders/delivery-check/${customerId}/?include_terminals=true`);
        setData(response.data);
      } catch (err: any) {
        if (err.response?.status !== 404) {
          setError('Could not check delivery info');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveryCheck();
  }, [customerId]);

  if (loading) {
    return (
      <div className={`animate-pulse bg-gray-100 rounded-lg p-3 ${className}`}>
        <div className="h-4 bg-gray-200 rounded w-3/4" />
      </div>
    );
  }

  if (error || !data || !data.has_zone) {
    return null;
  }

  // No warning needed for daily delivery without remote fee
  if (!data.warning_message && !data.remote_fee) {
    return null;
  }

  // Determine banner style based on warning level
  const getBannerStyles = () => {
    switch (data.warning_level) {
      case 'critical':
        return {
          background: '#fef2f2',
          border: '2px solid #fca5a5',
          iconBg: '#fee2e2',
          textColor: '#991b1b',
          headingColor: '#7f1d1d',
        };
      case 'warning':
        return {
          background: '#fffbeb',
          border: '2px solid #fcd34d',
          iconBg: '#fef3c7',
          textColor: '#92400e',
          headingColor: '#78350f',
        };
      default:
        return {
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          iconBg: '#dbeafe',
          textColor: '#1e40af',
          headingColor: '#1e3a8a',
        };
    }
  };

  const bannerStyles = getBannerStyles();

  const getIcon = () => {
    switch (data.warning_level) {
      case 'critical':
        return '🚫';
      case 'warning':
        return '⚠️';
      default:
        return 'ℹ️';
    }
  };

  const handleWillCallClick = () => {
    if (onSuggestWillCall && data.nearest_terminals.length > 0) {
      onSuggestWillCall(data.nearest_terminals);
    }
    setShowTerminals(!showTerminals);
  };

  if (compact) {
    return (
      <div
        className={className}
        style={{
          background: bannerStyles.background,
          border: bannerStyles.border,
          borderRadius: '8px',
          padding: '12px',
          ...style,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>{getIcon()}</span>
          <span style={{ color: bannerStyles.textColor, fontSize: '14px' }}>
            {data.is_single_day
              ? `${data.delivery_days} only`
              : data.is_limited
              ? `Delivers ${data.delivery_days}`
              : data.needs_handoff
              ? 'Peninsula delivery (2-3 days)'
              : ''}
            {data.remote_fee && ` • $${data.remote_fee} remote fee`}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        background: bannerStyles.background,
        border: bannerStyles.border,
        borderRadius: '12px',
        padding: '16px',
        ...style,
      }}
    >
      <div style={{ display: 'flex', gap: '12px' }}>
        <div
          style={{
            background: bannerStyles.iconBg,
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '20px' }}>{getIcon()}</span>
        </div>

        <div style={{ flex: 1 }}>
          <h4
            style={{
              margin: '0 0 4px 0',
              fontWeight: 600,
              color: bannerStyles.headingColor,
              fontSize: '15px',
            }}
          >
            {data.is_single_day
              ? 'Single-Day Delivery Area'
              : data.is_limited
              ? 'Limited Delivery Days'
              : data.needs_handoff
              ? 'Peninsula Delivery'
              : 'Delivery Information'}
          </h4>

          <p
            style={{
              margin: '0',
              color: bannerStyles.textColor,
              fontSize: '14px',
              lineHeight: 1.5,
            }}
          >
            {data.warning_message}
          </p>

          {data.next_delivery_date && (
            <p
              style={{
                margin: '8px 0 0 0',
                color: bannerStyles.textColor,
                fontSize: '13px',
              }}
            >
              <strong>Next delivery:</strong> {data.next_delivery_date}
              {data.days_until_delivery !== null && (
                <span style={{ marginLeft: '4px' }}>
                  ({data.days_until_delivery} day{data.days_until_delivery !== 1 ? 's' : ''})
                </span>
              )}
            </p>
          )}

          {data.suggest_will_call && data.nearest_terminals.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <button
                type="button"
                onClick={handleWillCallClick}
                style={{
                  background: 'transparent',
                  border: `1px solid ${bannerStyles.textColor}`,
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: bannerStyles.textColor,
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                📍 {showTerminals ? 'Hide' : 'Show'} Terminal Pickup Options
              </button>

              {showTerminals && (
                <div style={{ marginTop: '12px' }}>
                  <p
                    style={{
                      margin: '0 0 8px 0',
                      fontSize: '13px',
                      color: bannerStyles.textColor,
                      fontWeight: 500,
                    }}
                  >
                    Nearest pickup terminals:
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {data.nearest_terminals.map((terminal) => (
                      <div
                        key={terminal.code}
                        style={{
                          background: 'rgba(255,255,255,0.7)',
                          borderRadius: '6px',
                          padding: '10px',
                          fontSize: '13px',
                        }}
                      >
                        <strong style={{ color: bannerStyles.headingColor }}>
                          {terminal.name} ({terminal.carrier})
                        </strong>
                        <br />
                        <span style={{ color: bannerStyles.textColor }}>{terminal.address}</span>
                        {terminal.phone && (
                          <>
                            <br />
                            <span style={{ color: bannerStyles.textColor }}>
                              📞 {terminal.phone}
                            </span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryWarningBanner;
